import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { SeverityBadge } from '@/components/domain/SeverityBadge';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { useAlerts, useCrashes, useDevices } from '@/features/shared/queries';
import { absoluteTime, confidencePercent, coordinates } from '@/lib/format';

export function CrashListPage() {
  const crashes = useCrashes(100);
  const devices = useDevices();
  const alerts = useAlerts(500);

  const deviceById = new Map((devices.data ?? []).map((device) => [device.id, device]));

  return (
    <>
      <PageHeader
        title="Crash events"
        description="Impacts that cleared the 3.5 g threshold and were scored above normal."
      />

      <Card>
        <CardHeader title="All events" description="Newest first." />

        {crashes.isPending ? (
          <SkeletonRows rows={5} />
        ) : crashes.isError ? (
          <ErrorState error={crashes.error} onRetry={crashes.refetch} />
        ) : (crashes.data ?? []).length === 0 ? (
          <EmptyState
            icon={ShieldAlert}
            title="No crash events"
            description="Nothing has been detected on your vehicles. Events appear here with the alerts they triggered."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Severity</th>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="hidden px-4 py-2 font-medium sm:table-cell">Device</th>
                  <th className="hidden px-4 py-2 font-medium md:table-cell">Location</th>
                  <th className="px-4 py-2 font-medium">Alerts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {(crashes.data ?? []).map((crash) => {
                  const related = (alerts.data ?? []).filter(
                    (alert) => alert.crash_event_id === crash.id
                  );
                  const failed = related.filter((alert) => alert.status === 'failed').length;
                  const device = deviceById.get(crash.device_id);

                  return (
                    <tr key={crash.id} className="transition-colors hover:bg-surface-sunken">
                      <td className="px-4 py-2.5">
                        <Link to={`/crashes/${crash.id}`}>
                          <SeverityBadge severity={crash.severity} />
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5" title={absoluteTime(crash.timestamp)}>
                        <Link to={`/crashes/${crash.id}`} className="text-ink hover:text-accent">
                          <RelativeTime value={crash.timestamp} />
                        </Link>
                      </td>
                      <td className="hidden px-4 py-2.5 text-ink-secondary sm:table-cell">
                        {device?.serial_number ?? `Device ${crash.device_id}`}
                      </td>
                      <td className="hidden px-4 py-2.5 text-ink-secondary md:table-cell" data-numeric>
                        {coordinates(crash.gps_lat, crash.gps_lon)}
                      </td>
                      <td className="px-4 py-2.5">
                        {related.length === 0 ? (
                          <span className="text-ink-muted">—</span>
                        ) : failed > 0 ? (
                          <span className="font-medium text-bad">
                            {failed} of {related.length} failed
                          </span>
                        ) : (
                          <span className="text-ok">All delivered</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-3 text-[12px] text-ink-muted">
        Severity is scored from impact force and speed by a rule-based engine. Confidence values
        shown on an event refer to that scoring, e.g.{' '}
        {confidencePercent(0.93)} on a critical impact.
      </p>
    </>
  );
}
